import { ChevronRight, DragHandle } from '@mui/icons-material';
import { MenuItem, Tooltip } from '@mui/material';
import clsx from 'clsx';
import { MenuIcon } from 'components/buttons';
import { GridItemType, IFrameGridItemConfig } from 'components/grid';
import { DRAG_DATA_KEY, WORKSPACES_PAGE_ROUTE } from 'const';
import { useHasPrivileges } from 'kineticUtils';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, Route, Switch } from 'react-router-dom';
import { generate as shortId } from 'shortid';

import { KineticModule } from '@kinetic-ui/data-layer';
import { useModuleLicense } from 'contexts/hooks/useModuleLicense';
import useDrawerMenuItemStyles from './styles';
import { DrawerMenuItemProps } from './types';

const DrawerMenuItem = React.forwardRef<HTMLAnchorElement | HTMLLIElement, DrawerMenuItemProps>(
  (props, ref) => {
    const {
      children,
      to,
      tooltip,
      hasSubmenu,
      onClick,
      dragImageUrl,
      dragData,
      icon,
      iconHasTooltip,
      draggable = true,
      id,
      module,
    } = props;

    const classes = useDrawerMenuItemStyles(props);
    const hasPrivilege = useHasPrivileges(module);
    const hasModuleLicense = useModuleLicense(module as KineticModule);

    const dragImage = useRef<HTMLImageElement>();
    const [hideTooltip, setHideTooltip] = useState(false);

    const onMouseEnter = useCallback(() => {
      if (iconHasTooltip) {
        setHideTooltip(true);
      }
    }, [iconHasTooltip]);

    const onMouseLeave = useCallback(() => {
      if (iconHasTooltip) {
        setHideTooltip(false);
      }
    }, [iconHasTooltip]);

    useEffect(() => {
      if (dragImageUrl) {
        const img = new Image();
        img.src = dragImageUrl;
        img.onload = () => (dragImage.current = img);
      }
    }, [dragImageUrl]);

    const onDragStart = useCallback(
      (event: React.DragEvent) => {
        if (!draggable) {
          return;
        }
        if (dragImage.current) {
          event.dataTransfer.setDragImage(dragImage.current, -2, 0);
        }

        const gridItemData: IFrameGridItemConfig = {
          id: shortId(),
          type: GridItemType.IFRAME,
          title: children.toString(),
          data: {
            to,
          },
          ...dragData,
        };

        event.dataTransfer.setData(DRAG_DATA_KEY, JSON.stringify(gridItemData));
      },
      [children, dragData, to, draggable]
    );

    const wrappedIcon = useMemo(
      () => (
        <span
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
          className={classes.iconWrapper}
        >
          {icon}
        </span>
      ),
      [classes.iconWrapper, icon, onMouseEnter, onMouseLeave]
    );

    // if on workspaces route, show the dragHandle icon.
    // probably should be tied to draggable state, not route. draggable prop confusing
    const linkIcon = useMemo(() => {
      return (
        <Switch>
          {draggable && (
            <Route path={`/${WORKSPACES_PAGE_ROUTE}/`}>
              <MenuIcon>
                <DragHandle />
              </MenuIcon>
            </Route>
          )}
          <Route>{wrappedIcon}</Route>
        </Switch>
      );
    }, [draggable, wrappedIcon]);

    const commonProps = {
      id,
      disableGutters: true,
      className: clsx(classes.menuItem, classes.subtleIcon, {
        [classes.disabledLink]: !hasPrivilege,
        [classes.selected]: props.selected,
      }),
      // MenuItem/ListItem prevents dragging unless you pass `draggable`.
      draggable,
      onDragStart,
      // Material types for MenuItem are different depending on component. either should be fine
      ref: ref as never,
      // Disable onClick if user does not have privilege
      onClick: hasPrivilege ? onClick : null,
    } as any;

    // Could/should be new component?
    const renderedItem = to ? (
      <MenuItem {...commonProps} component={Link} to={to} role='listitem'>
        {children} {linkIcon}
      </MenuItem>
    ) : (
      <MenuItem {...commonProps} disableRipple={!hasSubmenu} role='listitem'>
        {children}
        {hasSubmenu ? (
          <MenuIcon>
            <ChevronRight />
          </MenuIcon>
        ) : (
          wrappedIcon
        )}
      </MenuItem>
    );

    // don't render drawer menu item if it is tied to a module and the user doesn't have a license for that module
    return !hasModuleLicense || !hasPrivilege ? null : (
      <Tooltip title={(!hideTooltip && tooltip) || ''} placement='right'>
        <ul className={classes.drawerList}>{renderedItem}</ul>
      </Tooltip>
    );
  }
);

export default DrawerMenuItem;
